import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  HostListener,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class FilterBarComponent implements OnInit, OnChanges {
  ngOnChanges(): void {}

  /**
   * Defensive: Always render checkboxes for the union of parentJobCodes and selectedParentJobCodes.
   * This ensures all selected codes are visible even if not present in the current options.
   */
  get parentJobCodesUnion(): string[] {
    if (!this.parentJobCodes && !this.selectedParentJobCodes) return [];
    const arr = [...(this.parentJobCodes || []), ...(this.selectedParentJobCodes || [])];
    // Unique and sorted
    return Array.from(new Set(arr)).sort();
  }

  @Input() projectCountLabel: string = 'Projects';
  @Input() purchaseOrderCountLabel: string = 'POs';
  // Configuration inputs
  @Input() showFiscalYearFilter = true;
  @Input() showStatusFilter = true;
  @Input() showBillingStatusFilter = true;
  @Input() showParentJobCodeFilter = true;
  @Input() showJobCodeFilter = true;
  @Input() showSearchField = true;
  @Input() showBuyerFilter = true;
  @Input() showClientPoFilter = true; // Will be disabled for projects component
  @Input() showClientLeadFilter = true;
  @Input() showProgramManagerFilter = true;
  @Input() showEngagementLeadFilter = true;
  @Input() showProjectTypeFilter = true;
  @Input() showJobTypeFilter = true;

  // Data inputs
  @Input() fiscalYears: string[] = [];
  @Input() statusOptions: string[] = [];
  @Input() billingStatuses: string[] = [];
  @Input() parentJobCodes: string[] = [];
  @Input() jobCodes: string[] = [];
  @Input() buyers: string[] = [];
  @Input() clientPoNumbers: string[] = [];
  @Input() clientLeads: string[] = [];
  @Input() programManagers: string[] = [];
  @Input() engagementLeads: string[] = [];
  @Input() projectTypes: string[] = [];
  @Input() jobTypes: string[] = [];

  // Selected values inputs
  @Input() selectedFiscalYears: string[] = [];
  @Input() selectedStatuses: string[] = [];
  @Input() selectedBillingStatuses: string[] = [];
  @Input() selectedParentJobCodes: string[] = [];
  @Input() selectedJobCodes: string[] = [];
  @Input() selectedBuyers: string[] = [];
  @Input() selectedClientPoNumbers: string[] = [];
  @Input() selectedClientLeads: string[] = [];
  @Input() selectedProgramManagers: string[] = [];
  @Input() selectedEngagementLeads: string[] = [];
  @Input() selectedProjectTypes: string[] = [];
  @Input() selectedJobTypes: string[] = [];

  // Count inputs
  @Input() projectCount = 0;
  @Input() purchaseOrderCount = 0;
  @Input() showPurchaseOrderCount = true;

  // Search field
  @Input() searchTerm = '';
  @Output() searchChange = new EventEmitter<string>();

  // Output events
  @Output() fiscalYearChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() statusChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() billingStatusChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() parentJobCodeChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() jobCodeChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() buyerChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() clientPoChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() clientLeadChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() programManagerChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() engagementLeadChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() projectTypeChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() jobTypeChange = new EventEmitter<{
    value: string;
    checked: boolean;
  }>();
  @Output() resetFiltersEvent = new EventEmitter<void>();

  // Dropdown state
  dropdownOpen: { [key: string]: boolean } = {
    fy: false,
    status: false,
    billing: false,
    parentJobCode: false,
    jobCode: false,
    buyer: false,
    po: false,
    clientLead: false,
    programManager: false,
    engagementLead: false,
    projectType: false,
    jobType: false,
  };

  constructor(private elRef: ElementRef) {}

  ngOnInit(): void {}

  // Toggle a dropdown open/closed and close others
  toggleDropdown(type: string): void {
    for (const key in this.dropdownOpen) {
      if (key !== type) {
        this.dropdownOpen[key] = false;
      }
    }
    this.dropdownOpen[type] = !this.dropdownOpen[type];
  }

  // Close all dropdowns (for click-away)
  closeAllDropdowns(): void {
    for (const key in this.dropdownOpen) {
      this.dropdownOpen[key] = false;
    }
  }

  // Close dropdown when clicking outside any dropdown-group
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const dropdowns: NodeListOf<HTMLElement> =
      this.elRef.nativeElement.querySelectorAll('.dropdown-group');
    let clickedInside = false;
    dropdowns.forEach((d) => {
      if (d.contains(event.target as Node)) {
        clickedInside = true;
      }
    });
    if (!clickedInside) {
      this.closeAllDropdowns();
    }
  }

  // Filter toggle handlers
  toggleFiscalYear(value: string, checked: boolean): void {
    this.fiscalYearChange.emit({ value, checked });
  }

  toggleStatus(value: string, checked: boolean): void {
    this.statusChange.emit({ value, checked });
  }

  toggleBillingStatus(value: string, checked: boolean): void {
    this.billingStatusChange.emit({ value, checked });
  }

  toggleParentJobCode(value: string, checked: boolean): void {
    this.parentJobCodeChange.emit({ value, checked });
  }

  toggleJobCode(value: string, checked: boolean): void {
    this.jobCodeChange.emit({ value, checked });
  }

  toggleBuyer(value: string, checked: boolean): void {
    this.buyerChange.emit({ value, checked });
  }

  toggleClientPo(value: string, checked: boolean): void {
    this.clientPoChange.emit({ value, checked });
  }

  toggleClientLead(value: string, checked: boolean): void {
    this.clientLeadChange.emit({ value, checked });
  }

  toggleProgramManager(value: string, checked: boolean): void {
    this.programManagerChange.emit({ value, checked });
  }

  toggleEngagementLead(value: string, checked: boolean): void {
    this.engagementLeadChange.emit({ value, checked });
  }

  toggleProjectType(value: string, checked: boolean): void {
    this.projectTypeChange.emit({ value, checked });
  }

  toggleJobType(value: string, checked: boolean): void {
    this.jobTypeChange.emit({ value, checked });
  }

  resetFilters(): void {
    this.resetFiltersEvent.emit();
  }

  // Search input handler
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.searchChange.emit(this.searchTerm);
  }

  // Clear search field
  clearSearch(): void {
    this.searchTerm = '';
    this.searchChange.emit('');
  }
}
